import { CLASSIC_REWARD_PAYMENT_TYPE } from 'constants/payment'
import getOrderLoyaltyPendingPointsCampaignTypes from 'loyalty/lib/getOrderLoyaltyPendingPointsCampaignTypes'

/**
 * Adjusts pending-points totals to exclude Classic Rewards flights.
 *
 * Reward flights are booked with points + cash co-pay and must not accrue
 * earn on the cash portion. The backend currently includes pending-points
 * rows for all order items including reward flights, so the frontend
 * filters them out by matching against `is_classic_reward` on flight items.
 */
export function getClassicRewardAdjustedPendingPoints(order: App.Order): {
  points: number
  statusCredits: number
  bonusPointsCampaignTypes: Array<App.LoyaltyBonusPointsCampaignType>
} {
  const pendingPoints = order.loyaltyDetails.pendingPoints
  const items = pendingPoints?.items ?? []
  const rewardFlightItemIds = new Set(
    order.flightItems.filter(item => item.is_classic_reward).map(item => item.id),
  )

  if (rewardFlightItemIds.size === 0) {
    return {
      points: pendingPoints?.points ?? 0,
      statusCredits: pendingPoints?.statusCredits ?? 0,
      bonusPointsCampaignTypes: getOrderLoyaltyPendingPointsCampaignTypes(items),
    }
  }

  const filtered = items.filter(item => !rewardFlightItemIds.has(item.itemId))
  return {
    points: filtered.reduce((acc, item) => acc + item.points, 0),
    statusCredits: filtered.reduce((acc, item) => acc + item.statusCredits, 0),
    bonusPointsCampaignTypes: getOrderLoyaltyPendingPointsCampaignTypes(filtered),
  }
}

interface ClassicRewardOrderTotals {
  /** Sum of `points_price * quantity` across the payment's items_breakdown. */
  totalPoints: number
  /** Sum of `points_value * quantity` — the currency-equivalent that points covered. Should equal `payment.amount`. */
  totalPointsValue: number
}

/**
 * Aggregates points + currency value spent on Classic Reward redemptions for an order.
 *
 * Returns 0/0 when the order has no `le_points_classic_reward` payment or the wire
 * response did not include `item_le_points_details` (e.g. for older payments where
 * the field was not yet emitted).
 */
export function getClassicRewardTotalsFromOrder(order: App.Order): ClassicRewardOrderTotals {
  const result: ClassicRewardOrderTotals = { totalPoints: 0, totalPointsValue: 0 }
  for (const payment of order.payments) {
    if (payment.type !== CLASSIC_REWARD_PAYMENT_TYPE) continue
    if (payment.status === 'failed') continue
    for (const breakdownItem of payment.itemsBreakdown ?? []) {
      const { pointsPrice, pointsValue, quantity } = breakdownItem.lePointsDetails ?? {}

      if (pointsPrice == null || pointsValue == null || quantity == null) {
        continue
      }

      result.totalPoints += pointsPrice * quantity
      result.totalPointsValue += pointsValue * quantity
    }
  }
  return result
}
